import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Trash2, ArrowUpDown } from 'lucide-react';
import type { SubscriptionSensor } from '../../types';

const SENSOR_STATUS_LABELS: Record<number, { label: string; variant: 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' }> = {
  0: { label: 'Unmanaged', variant: 'secondary' },
  1: { label: 'Active', variant: 'success' },
  2: { label: 'Too Low', variant: 'warning' },
  3: { label: 'Too High', variant: 'warning' },
  4: { label: 'To Acknowledge', variant: 'warning' },
  5: { label: 'Inactive', variant: 'destructive' },
  6: { label: 'Maintenance', variant: 'outline' },
  8: { label: 'Inhibited', variant: 'secondary' },
  9: { label: 'Archived', variant: 'secondary' },
};

interface SubscriptionSensorsTabProps {
  sensors: SubscriptionSensor[];
  isLoading: boolean;
  onSelectSensors: () => void;
  onRemoveSensor: (sensorId: string) => void;
  onRemoveAllSensors: () => void;
}

export function SubscriptionSensorsTab({
  sensors,
  isLoading,
  onSelectSensors,
  onRemoveSensor,
  onRemoveAllSensors,
}: SubscriptionSensorsTabProps) {
  const { t } = useTranslation('subscriptions');
  const [filterQuery, setFilterQuery] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
  };

  const filteredSensors = useMemo(() => {
    let list = sensors;

    if (filterQuery) {
      const q = filterQuery.toLowerCase();
      list = list.filter((s) =>
        (s.name || '').toLowerCase().includes(q) ||
        (s.customId || '').toLowerCase().includes(q) ||
        (s.location?.name || '').toLowerCase().includes(q) ||
        (s.streamName || '').toLowerCase().includes(q)
      );
    }

    return [...list].sort((a, b) => {
      let aVal = '';
      let bVal = '';
      switch (sortKey) {
        case 'customId': aVal = a.customId || ''; bVal = b.customId || ''; break;
        case 'name': aVal = a.name || ''; bVal = b.name || ''; break;
        case 'location': aVal = a.location?.name || ''; bVal = b.location?.name || ''; break;
        case 'stream': aVal = a.streamName || ''; bVal = b.streamName || ''; break;
        default: aVal = a.name || ''; bVal = b.name || '';
      }
      return aVal.localeCompare(bVal) * sortDirection;
    });
  }, [sensors, filterQuery, sortKey, sortDirection]);

  const SortHead = ({ label, field }: { label: string; field: string }) => (
    <th
      className="text-left px-2 py-2 font-medium text-xs cursor-pointer select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {sortKey === field && <ArrowUpDown className="h-3 w-3" />}
      </div>
    </th>
  );

  if (isLoading) {
    return (
      <div className="space-y-3 mt-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">
          {t('sensors.count', { count: sensors.length })}
        </h3>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onSelectSensors}>
            <Plus className="h-4 w-4 mr-1" />
            {t('sensors.selectSensors')}
          </Button>
          {sensors.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={onRemoveAllSensors}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t('sensors.removeAll')}
            </Button>
          )}
        </div>
      </div>

      {/* Filter */}
      {sensors.length > 0 && (
        <Input
          placeholder={t('sensors.filter')}
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          className="h-8 text-sm max-w-[250px]"
        />
      )}

      {/* Table */}
      {sensors.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">
          {t('sensors.noSensors')}
        </p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <SortHead label={t('sensors.customId')} field="customId" />
                <SortHead label={t('sensors.name')} field="name" />
                <SortHead label={t('sensors.location')} field="location" />
                <SortHead label={t('sensors.stream')} field="stream" />
                <th className="text-left px-2 py-2 font-medium text-xs">{t('sensors.status')}</th>
                <th className="px-2 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSensors.map((sensor) => (
                <tr key={sensor.id} className="border-t">
                  <td className="px-2 py-1.5 text-xs">{sensor.customId || '\u2014'}</td>
                  <td className="px-2 py-1.5 text-xs font-medium">{sensor.name}</td>
                  <td className="px-2 py-1.5 text-xs">{sensor.location?.name || '\u2014'}</td>
                  <td className="px-2 py-1.5 text-xs">{sensor.streamName || '\u2014'}</td>
                  <td className="px-2 py-1.5 text-xs">
                    {sensor.status != null ? (() => {
                      const statusInfo = SENSOR_STATUS_LABELS[sensor.status];
                      return (
                        <Badge variant={statusInfo?.variant ?? 'outline'} className="text-[10px]">
                          {statusInfo?.label ?? sensor.status}
                        </Badge>
                      );
                    })() : '\u2014'}
                  </td>
                  <td className="px-2 py-1.5">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-muted-foreground hover:text-destructive"
                      onClick={() => onRemoveSensor(sensor.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
